import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { useEffect, useState } from "react";
import GfgModal from "@/components/GfgModal";
import { PLAN_DOWNGRADE_MODAL_STRINGS, PLAN_ENTERPRISE_MODAL_STRINGS } from "@/constants/Plans";
import type { PlanModalConfig, PlanModalContext } from "@/interfaces/plan";
import { formatDateLong } from "@/utils/date-format";

/**
 * Every account-related GfgModal, driven by one config map — the CampaignBuilderModal pattern.
 * A new modal is a new key in `PlanModalContext` plus its entry here; no new component, and the
 * copy stays in constants/Plans.ts.
 */
const CONFIGS: Record<PlanModalContext, PlanModalConfig> = {
  downgrade: {
    icon: <InfoOutlinedIcon className="text-[28px] text-dark-dust" />,
    title: PLAN_DOWNGRADE_MODAL_STRINGS.title,
    description: ({ renewalDate, planName }) => (
      <div className="flex w-full flex-col gap-[12px]">
        <p className="text-center font-lato text-base leading-[normal] text-graphite">
          {PLAN_DOWNGRADE_MODAL_STRINGS.body(planName)}
        </p>
        <div className="flex items-start justify-center gap-[12px] rounded-[10px] border border-good-ochre-border bg-good-ochre-bg px-[16px] py-[14px]">
          <ScheduleOutlinedIcon className="shrink-0 text-[22px] text-good-ochre" />
          <p className="text-center font-lato text-base leading-normal text-good-ochre">
            {PLAN_DOWNGRADE_MODAL_STRINGS.callout(formatDateLong(renewalDate), planName)}
          </p>
        </div>
      </div>
    ),
    primaryLabel: PLAN_DOWNGRADE_MODAL_STRINGS.confirmButton,
    secondaryLabel: ({ planName }) => PLAN_DOWNGRADE_MODAL_STRINGS.keepButton(planName),
  },
  enterprise: {
    icon: <InfoOutlinedIcon className="text-[28px] text-dark-dust" />,
    title: PLAN_ENTERPRISE_MODAL_STRINGS.title,
    description: () => PLAN_ENTERPRISE_MODAL_STRINGS.body,
    primaryLabel: PLAN_ENTERPRISE_MODAL_STRINGS.dismissButton,
  },
};

export interface PlanActionModalProps {
  /** null closes the modal; the context picks which one renders. */
  context: PlanModalContext | null;
  /**
   * GfgModal fires `onClose` before `onPrimary`, so this runs on confirm too.
   * Keep it a pure dismiss.
   */
  onClose: () => void;
  /** The context's primary action. Informational contexts pass a no-op. */
  onPrimary: () => void;
  renewalDate: string | null;
  /** Catalogue name of the current plan — the downgrade copy names what is being given up. */
  planName: string;
}

export default function PlanActionModal({
  context,
  onClose,
  onPrimary,
  renewalDate,
  planName,
}: PlanActionModalProps) {
  // Freeze the context while the close transition plays so the content doesn't flash —
  // same pattern as CampaignBuilderModal.
  const [stableContext, setStableContext] = useState<PlanModalContext>(context ?? "downgrade");
  useEffect(() => {
    if (context !== null) setStableContext(context);
  }, [context]);

  const config = CONFIGS[stableContext];

  return (
    <GfgModal
      open={context !== null}
      onClose={onClose}
      onPrimary={onPrimary}
      icon={config.icon}
      title={config.title}
      description={config.description({ renewalDate, planName })}
      primaryLabel={config.primaryLabel}
      secondaryLabel={config.secondaryLabel?.({ renewalDate, planName })}
    />
  );
}
